/**
 * 自由绘制元素（涂抹笔）
 *
 * 渲染策略（与 VectorElement 相同）：
 *  - viewBox="0 0 100 100" + preserveAspectRatio="none"
 *      → 路径随元素宽高缩放，支持用户拖拽调整大小
 *  - vectorEffect="non-scaling-stroke"
 *      → 描边宽度始终为 strokeWidth CSS px，不受 SVG viewBox 缩放影响，
 *        因此不因元素缩放或长宽比变化而失真
 *
 * 线宽与预览一致的保证：
 *  预览时 SVG 使用 strokeWidth（画布像素）→ CSS zoom 后 = strokeWidth×zoom 屏幕像素
 *  提交后 vectorEffect 使描边 = strokeWidth CSS px → CSS zoom 后 = strokeWidth×zoom 屏幕像素
 *  两者在任意缩放比下完全一致。
 */
import type { FreehandElement as FreehandElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../../group/containerTransform';
import { blurRadius, firstStroke } from '@/components/canvas/elements/model';

export function FreehandElement({
  element,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ElementComponentProps<FreehandElementType>) {
  const { dx, dy } = dragOffset;
  const el = element;
  const stroke = firstStroke(el.appearance);

  if (!el.data.pathData) return null;

  return (
    <svg
      width={el.size.width}
      height={el.size.height}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        left: el.layout.x + dx,
        top: el.layout.y + dy,
        overflow: 'visible',
        opacity: el.appearance.opacity,
        transform: composeContainerTransform(el),
        filter: appendBlurFilter(undefined, blurRadius(el)),
        cursor: 'inherit',
      }}
      data-element-hit
      data-element-id={el.id}
      onClick={(e) => { e.stopPropagation(); onSelect(e); }}
      onMouseDown={(e) => onMouseDown?.(e, element)}
    >
      <path
        d={el.data.pathData}
        fill="none"
        stroke={stroke?.color ?? '#000'}
        strokeWidth={stroke?.width ?? 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
